import React, { useState } from "react";
import FormStruct from "@/components/Form/FormStruct/FormStruct";
import { Field, FormRenderProps } from "react-final-form";
import BFDocumentEditor from "@/modules/abstract-ui/forms/document-editor/BFDocumentEditor";
import FormFieldValues from "@/components/Form/Fields/FormFieldValues";
import "../contract/VacancyContractForm.scss";
import i18n from "@/i18n";
import { FV } from "@/components/Form/Validation/FormValidators";
import BFSelect from "@/modules/abstract-ui/forms/select/BFSelect";
import VacancyContractCancelButton from "../VacancyContractCancelButton";
import { VacancyContractTemplatePartType, VacancyContractSummaryFormType } from '../../../../model/VacancyApplicant.interface';
import VacancyContractUtils from '../../../../vacancy-applicant/utils/VacancyContractUtils';

type Props = {
  replacements: any;
  conditions?: { [key: string]: boolean };
  contractStateTemplate: VacancyContractTemplatePartType;
  depositTemplate: VacancyContractTemplatePartType;
  houseRulesTemplate: VacancyContractTemplatePartType;
  onSubmit: (values: any) => void;
  goBack?: () => void;
  tenantCount: number;
};

const VacancyContractSummaryForm = (props: Props) => {
  const {
    replacements,
    contractStateTemplate,
    depositTemplate,
    houseRulesTemplate,
    onSubmit,
    goBack,
    conditions,
    tenantCount,
  } = props;

  const [initialValues] = useState<
    Partial<VacancyContractSummaryFormType> | undefined
  >({
    contractContent: contractStateTemplate.templateContent,
    depositContent: depositTemplate.templateContent,
    houseRulesContent: houseRulesTemplate.templateContent,
    currentField: "contractContent",
  });

  return (
    <div className="vacancy-applicant-contract-form">
      <FormStruct
        additionalActions={<VacancyContractCancelButton />}
        initialValues={initialValues}
        submitText={i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Label.SaveAndSend",
          "Vertrag speichern und senden"
        )}
        onAbort={goBack}
        cancelText={i18n.t("Global.Buttons.back")}
        abortBtnProps={{
          appearance: "outline",
          onClick: goBack,
        }}
        render={(formRenderProps: FormRenderProps<any>) => {
          return (
            <div className="contract-summary-form-content">
              <div className="__h2">
                {i18n.t(
                  "apTemplate:Vacancy.Applicant.Contract.Label.SummaryTitle",
                  "Zusammenfassung"
                )}
              </div>
              <div>
                {i18n.t(
                  "apTemplate:Vacancy.Applicant.Contract.Label.SummaryMessage",
                  "Hier haben Sie die Möglichkeit, sämtliche Vertragsbestandteile nochmals zu überprüfen und bei Bedarf manuelle Anpassungen vorzunehmen. Bitte wählen Sie zum Bearbeiten den entsprechenden Vertragsbestandteil aus."
                )}
              </div>
              <div className="contract-form-fields">
                <div className="__form-row padding-10 __align-center">
                  <Field name="currentField">
                    {({ input }) => (
                      <BFSelect
                        cleanable={false}
                        label={i18n.t(
                          "apTemplate:Vacancy.Applicant.Contract.Label.SummaryPart",
                          "Vertragsbestandteil"
                        )}
                        data={VacancyContractUtils.getContractSummaryOptions()}
                        {...input}
                      />
                    )}
                  </Field>

                  <Field
                    name="pdfCreation"
                    validate={FV.compose(
                      FV.required(
                        i18n.t(
                          "apTemplate:Vacancy.Applicant.Contract.Label.DigitalSiginError",
                          "Bitte geben Sie eine Unterschrifart an."
                        )
                      )
                    )}
                  >
                    {({ input, meta }) => (
                      <BFSelect
                        cleanable={false}
                        data={VacancyContractUtils.getContractDigitalSignOptions()}
                        label={i18n.t(
                          "apTemplate:Vacancy.Applicant.Contract.Label.DigitalSiginError",
                          "Bitte geben Sie eine Unterschrifart an."
                        )}
                        {...input}
                        validation={FV.getValidation(meta)?.validation}
                      />
                    )}
                  </Field>
                </div>
                <FormFieldValues names={["currentField"]}>
                  {([currentField]) => {
                    return (
                      <>
                        <Field name="contractContent">
                          {({ input, meta }) => (
                            <div
                              style={{
                                display:
                                  formRenderProps.values.currentField ===
                                  "contractContent"
                                    ? "block"
                                    : "none",
                              }}
                            >
                              <BFDocumentEditor
                                height={875}
                                {...input}
                                suffixHtml={
                                  contractStateTemplate.templateSuffix
                                }
                                prefixHtml={
                                  contractStateTemplate.templatePrefix
                                }
                                resolvedVariables={replacements}
                                resolvedVariableFormatters={VacancyContractUtils.getReplacementFormaters()}
                                variableSource={async (search) => {
                                  return VacancyContractUtils.getEditorVariableList(
                                    tenantCount,
                                    search
                                  );
                                }}
                                conditions={conditions}
                              />
                            </div>
                          )}
                        </Field>
                        <Field name="depositContent">
                          {({ input, meta }) => (
                            <div
                              style={{
                                display:
                                  formRenderProps.values.currentField ===
                                  "depositContent"
                                    ? "block"
                                    : "none",
                              }}
                            >
                              <BFDocumentEditor
                                height={875}
                                {...input}
                                suffixHtml={depositTemplate.templateSuffix}
                                prefixHtml={depositTemplate.templatePrefix}
                                resolvedVariables={replacements}
                                resolvedVariableFormatters={VacancyContractUtils.getReplacementFormaters()}
                                variableSource={async (search) => {
                                  return VacancyContractUtils.getEditorVariableList(
                                    tenantCount,
                                    search
                                  );
                                }}
                              />
                            </div>
                          )}
                        </Field>
                        <Field name="houseRulesContent">
                          {({ input, meta }) => (
                            <div
                              style={{
                                display:
                                  formRenderProps.values.currentField ===
                                  "houseRulesContent"
                                    ? "block"
                                    : "none",
                              }}
                            >
                              <BFDocumentEditor
                                height={875}
                                {...input}
                                suffixHtml={houseRulesTemplate.templateSuffix}
                                prefixHtml={houseRulesTemplate.templatePrefix}
                                resolvedVariables={replacements}
                                resolvedVariableFormatters={VacancyContractUtils.getReplacementFormaters()}
                                variableSource={async (search) => {
                                  return VacancyContractUtils.getEditorVariableList(
                                    tenantCount,
                                    search
                                  );
                                }}
                              />
                            </div>
                          )}
                        </Field>
                      </>
                    );
                  }}
                </FormFieldValues>
              </div>
            </div>
          );
        }}
        onSubmit={(data: VacancyContractSummaryFormType) => {
          onSubmit(data);
        }}
      />
    </div>
  );
};

export default VacancyContractSummaryForm;
